import type { BankAccountValues, BankOnlineValues } from "./bankAccountTypes";

/** Ländercode und Prüfziffern vor der BBAN. */
export const IBAN_PREFIX_LENGTH = 4;
/** Deutsche Bankleitzahl: 8 Stellen direkt nach Ländercode und Prüfziffern. */
export const GERMAN_BANK_CODE_LENGTH = 8;
/** ISO 13616: Buchstaben werden zu Zahlen (A = 10), Prüfsumme modulo 97. */
const LETTER_TO_DIGITS_OFFSET = 55;
const IBAN_MODULUS = 97;
const DECIMAL_BASE = 10;

/** Frontend convenience validation; the owning backend remains authoritative. */
export const normalizeBankCode = (value: string) => String(value ?? "").replace(/\s+/g, "").toUpperCase();
export function validBankIban(value: string) {
  const iban = normalizeBankCode(value);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  let mod = 0;
  for (const char of iban.slice(IBAN_PREFIX_LENGTH) + iban.slice(0, IBAN_PREFIX_LENGTH)) {
    const number = char >= "A" ? String(char.charCodeAt(0) - LETTER_TO_DIGITS_OFFSET) : char;
    for (const digit of number) mod = (mod * DECIMAL_BASE + Number(digit)) % IBAN_MODULUS;
  }
  return mod === 1;
}
export const validBankBic = (value: string) => /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(normalizeBankCode(value));
export function validBankCreditorId(value: string) {
  const id = normalizeBankCode(value);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{3}[A-Z0-9]{1,28}$/.test(id)) return false;
  return true;
}

/** Bank-provided PIN/TAN and key-file transports use different endpoints for this bank. */
export function bankingAddressForAccessMethod(method: 0 | 1 | 2, address: string) {
  if (!address.includes("hypovereinsbank.de")) return address;
  return method === 0 ? "https://hbci-01.hypovereinsbank.de/bank/hbci" : "hbci.hypovereinsbank.de";
}

/** Canonical draft normalization; adapters only map optional wire values and product DTOs. Password bytes stay untouched. */
export function normalizeBankAccountValues(values: BankAccountValues): BankAccountValues {
  return { ...values, name: values.name.trim(), accountHolder: values.accountHolder.trim(), iban: normalizeBankCode(values.iban),
    bic: normalizeBankCode(values.bic), bankName: values.bankName.trim(), creditorId: normalizeBankCode(values.creditorId),
    online: values.online.map(value => ({ ...value, name: value.name.trim(), blz: value.blz.trim(), url: value.url.trim(),
      userId: value.userId.trim(), customerId: value.customerId.trim() })) };
}

/** Required setup is independent of activation; PIN entry belongs to the bank dialog. */
export function bankOnlineSetupComplete(value: BankOnlineValues | undefined, named: boolean, file?: File): boolean {
  return !!value && (!named || !!value.name.trim()) && /^[0-9]{8}$/.test(value.blz.trim()) && !!value.url.trim()
    && (value.accessMethod === 0 ? !!value.userId.trim() : value.hasStoredPassport || !!file)
    && (!file || !!value.filePassword.trim());
}
